<template>
  <div>
    <h2>{{ t('blockedDeposit') }}</h2>
    <div v-if="booking?.hasBlockedCreditCard && booking?.blockedCreditCardRefNr">
      <div class="mt-5 grid gap-4 rounded-lg bg-white px-2 pb-1 pt-1.5 md:grid-cols-4">
        <div>
          {{ t('blockedAmount') }}
          <span
            v-if="booking.blockedCreditCardAmount"
            v-currency="booking.blockedCreditCardAmount"
            class="font-medium"
          ></span>
          <span v-else class="font-medium">-</span>
        </div>
        <div>
          {{ t('blockedAt') }}
          <span class="font-medium">{{
            formatDate(booking.blockedCreditCardDate, dateFormat)
          }}</span>
        </div>
        <div>
          {{ t('source') }}
          <span class="font-medium">{{ booking.blockedCreditCardDetails?.source ?? '-' }}</span>
        </div>
        <div>
          {{ t('refNumber') }}
          <span class="font-medium">{{ booking.blockedCreditCardRefNr }}</span>
        </div>
      </div>
      <div class="mt-4 flex justify-end">
        <CVButton
          v-if="booking.blockedCreditCardDetails"
          @click.prevent="showCreditCardDetails = true"
        >
          {{ t('showCreditCardDetails') }}
        </CVButton>
      </div>
    </div>
    <div v-else class="mt-4">
      <p class="mb-4">{{ t('noBlockedDeposit') }}</p>
      <div
        v-if="booking.depositPaymentLinkUrl || booking.flags?.createdDepositPaymentLinkDate"
        class="mb-4"
      >
        <LabeledText
          :label="`${t('depositPaymentLink')} (${formatDate(booking.flags.createdDepositPaymentLinkDate, dateFormat)})`"
        >
          <div class="space-y-1">
            <div v-if="booking.depositPaymentLinkUrl" class="flex min-w-0 items-center gap-1">
              <span class="min-w-0 truncate" :title="booking.depositPaymentLinkUrl">{{
                booking.depositPaymentLinkUrl
              }}</span>
              <CopyToClipboardButton
                :text="booking.depositPaymentLinkUrl"
                class="shrink-0 text-obsidian-500"
              />
            </div>
          </div>
        </LabeledText>
      </div>
      <div class="flex justify-end">
        <CVButton
          :is-loading="isBlockingAmount"
          :outline="!!booking.flags?.createdDepositPaymentLinkDate"
          :disabled="paymentLinkSent"
          class="max-sm:w-full"
          @click.prevent="sendPaymentLink"
          >{{ t('sendDepositPaymentLink') }}</CVButton
        >
      </div>
    </div>
    <Modal
      v-if="booking.blockedCreditCardDetails"
      v-model="showCreditCardDetails"
      :show-close="true"
    >
      <template #title>{{ t('creditCardDetails') }}</template>
      <div class="mt-4">
        <CreditCardDetails :credit-card-details="booking.blockedCreditCardDetails" />
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import type { Booking } from '@/entities/bookings/booking.entity';
import { useDateFormat } from '@/hooks/use-date-format';
import { useI18n } from 'vue-i18n';
import { useBlockAmount } from '@/queries/use-payment-api';
import { ref } from 'vue';
import CVButton from '@/components/buttons/CVButton.vue';
import CreditCardDetails from './CreditCardDetails.vue';
import Modal from '@/components/Modal.vue';
import LabeledText from '@/components/LabeledText.vue';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import {
  selectPaymentLinkReceiver,
  showPaymentLinkToCopy,
} from '@/utils/select-payment-link-receiver';
import { SendPaymentLinkResult } from '@carvia/ros-client-types';
import { formatDate } from '@/hooks/use-formatted-date';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();
const dateFormat = useDateFormat();

const paymentLinkSent = ref(false);
const showCreditCardDetails = ref(false);

const { mutateAsync: blockAmount, isPending: isBlockingAmount } = useBlockAmount();

const sendPaymentLink = async () => {
  const paymentLinkReceiver = await selectPaymentLinkReceiver(props.booking);
  if (!paymentLinkReceiver) return;

  const { paymentLink } = await blockAmount({
    bookingId: props.booking.id,
    amount: props.booking.priceCalculation.deposit,
    paymentLinkReceiver,
  });
  paymentLinkSent.value = true;
  if (paymentLink?.sendPaymentLinkResult === SendPaymentLinkResult.DISABLED) {
    showPaymentLinkToCopy(paymentLink.url);
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "blockedDeposit": "Blocked Deposit",
    "blockedAmount": "Blocked Amount:",
    "blockedAt": "Blocked at:",
    "refNumber": "Ref Nr:",
    "source": "Source:",
    "sendDepositPaymentLink": "+ Send deposit payment link",
    "noBlockedDeposit": "This booking has no blocked deposit",
    "depositPaymentLink": "Last deposit payment link",
    "creditCardDetails": "Credit Card Details",
    "close": "Close",
    "showCreditCardDetails": "Show credit card details"
  },
  "de": {
    "blockedDeposit": "Hinterlegte Kaution",
    "blockedAmount": "Blockierter Betrag:",
    "blockedAt": "Blockiert am:",
    "refNumber": "Refnr:",
    "source": "Source:",
    "sendDepositPaymentLink": "+ Kaution Zahlungslink senden",
    "noBlockedDeposit": "Diese Buchung hat keine hinterlegte Kaution",
    "depositPaymentLink": "Letzter Kaution Zahlungslink",
    "creditCardDetails": "Kreditkarten Details",
    "close": "Schließen",
    "showCreditCardDetails": "Kreditkarten Details anzeigen"
  },
  "es": {
    "blockedDeposit": "Depósito retenido",
    "blockedAmount": "Importe retenido:",
    "blockedAt": "Retenido el:",
    "refNumber": "N.º ref.:",
    "source": "Origen:",
    "sendDepositPaymentLink": "+ Enviar enlace de pago del depósito",
    "noBlockedDeposit": "Esta reserva no tiene depósito retenido",
    "depositPaymentLink": "Último enlace de pago del depósito",
    "creditCardDetails": "Datos de la tarjeta",
    "close": "Cerrar",
    "showCreditCardDetails": "Mostrar datos de la tarjeta"
  }
}
</i18n>
